import PropTypes from 'prop-types';
import React from 'react';
import isEqual from 'react-fast-compare';
import {connect} from 'react-redux';
import {compose} from 'redux';
import _ from 'underscore';

import {Batch} from 'constants/SampleConstants';
import {LoadingFileScreen} from 'nav/full-screen/LoadingFullScreen';
import {BenchlingState} from 'redux-stores/reduceApp';
import {REQUEST_FULFILLMENT_SHAPE, RequestFulfillment} from 'requests/fulfillments/FulfillmentConstants';
import {fetchRequestResponse} from 'requests/fulfillments/FulfillmentReduxActions';
import {
  getLegacyRequestObjectName,
  Request,
  REQUEST_RESPONSE_SHAPE,
  REQUEST_SCHEMA_SHAPE,
  REQUEST_SHAPE,
  RequestResponseSample,
  RequestSchema,
} from 'requests/RequestConstants';
import {ASSAY_TYPES} from 'results/assay-schemas/AssaySchemaConstants';
import StandaloneResultsView from 'results/assays/StandaloneResultsView';
import * as AssaySchemaActions from 'results/AssaySchemaActions';
import AssayStore from 'results/AssayStore';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import StorableStore from 'samples/StorableStore';
import * as Headers from 'ui-library/Headers';

import FulfillmentLinks from './FulfillmentLinks';
import ResponseSamples from './ResponseSamples';
import withRequestLoading from './withRequestLoading';

interface FrameProps {
  children: React.ReactNode;
  request: Request;
  requestSchema: RequestSchema;
}

function Frame(props: FrameProps): JSX.Element {
  return (
    <div className="react-tab-container react-tab-container--mediocre-contentWrapper">
      <div>
        <Headers.Header1>
          {props.requestSchema.name} {props.request.name}
        </Headers.Header1>
        {props.children}
      </div>
    </div>
  );
}
Frame.propTypes = {
  children: PropTypes.node.isRequired,
  request: REQUEST_SHAPE.isRequired,
  requestSchema: REQUEST_SCHEMA_SHAPE.isRequired,
};

interface BaseRequestResponseProps {
  request: Request | null;
  requestId: string;
  requestSchemas: Array<RequestSchema>;
  fetchRequestResponse: (requestId: string) => void;
  batches?: {[batchId: string]: Batch};
}

interface ConnectedProps {
  fulfillments?: Array<RequestFulfillment> | null;
  response: {samples: Array<RequestResponseSample>} | null;
}

type RequestResponseProps = BaseRequestResponseProps & ConnectedProps;

export class RequestResponse extends React.Component<RequestResponseProps> {
  static propTypes = {
    fulfillments: PropTypes.arrayOf(REQUEST_FULFILLMENT_SHAPE.isRequired),
    request: REQUEST_SHAPE,
    requestId: PropTypes.string.isRequired,
    response: REQUEST_RESPONSE_SHAPE,
    requestSchemas: PropTypes.arrayOf(REQUEST_SCHEMA_SHAPE.isRequired),

    fetchRequestResponse: PropTypes.func.isRequired,
  };

  componentDidMount(): void {
    if (this.props.request && !this._getRequestSchema()) {
      AssaySchemaActions.fetchAllAssaySchemas();
    }

    this.props.fetchRequestResponse(this.props.requestId);
  }

  componentDidUpdate(prevProps: RequestResponseProps): void {
    if (this.props.request && !prevProps.request && !this._getRequestSchema()) {
      AssaySchemaActions.fetchAllAssaySchemas();
    }
  }

  shouldComponentUpdate(nextProps: RequestResponseProps): boolean {
    return !isEqual(this.props, nextProps);
  }

  // Should only be called if the request is loaded
  _getRequestSchema(): RequestSchema | undefined {
    return _.findWhere(this.props.requestSchemas, {id: this.props.request!.schemaId});
  }

  render(): JSX.Element {
    if (
      !this.props.fulfillments ||
      !this.props.request ||
      !this.props.requestSchemas ||
      !this.props.response
    ) {
      return <LoadingFileScreen />;
    }

    const requestSchema = this._getRequestSchema();
    if (!requestSchema) {
      return <LoadingFileScreen />;
    }

    const isExecuted = this.props.fulfillments.some(({entryId, workflowId}) => entryId || workflowId);
    if (!isExecuted) {
      return (
        <Frame request={this.props.request} requestSchema={requestSchema}>
          <div className="u-spacing-top u-faded">{`This ${getLegacyRequestObjectName({lowerCase: true})} has not been executed`}</div>
        </Frame>
      );
    }

    return (
      <Frame request={this.props.request} requestSchema={requestSchema}>
        <div className="requestView">
          <FulfillmentLinks requestId={this.props.requestId} />
          <ResponseSamples samples={this.props.response.samples} />
          <Headers.Title>{MODEL_NAMES.Results}</Headers.Title>
          <StandaloneResultsView
            emptyMessage="No results were created"
            filters={{requestId: this.props.requestId}}
            includeResultsAgainstArchivedSchemas={true}
          />
        </div>
      </Frame>
    );
  }
}

function mapStateToProps(state: BenchlingState, ownProps: BaseRequestResponseProps): ConnectedProps {
  const {fulfillmentsById, fulfillmentIdsByRequestId, responsesByRequestId} =
    state.models.requests.fulfillments;
  const {fileSummariesById} = state.models;
  const {batches} = ownProps;

  const response = responsesByRequestId[ownProps.requestId];
  if (!response) {
    return {response: null};
  }

  const samples = response.samples.map(({bioentityId, batchId, status}) => ({
    bioentity: bioentityId ? fileSummariesById[bioentityId] : null,
    // @ts-expect-error _.findWhere actually works on objects but the types expect a list
    batch: batchId ? (_.findWhere(batches, {id: batchId}) as Batch) : null,
    status,
  }));

  const fulfillmentIds = fulfillmentIdsByRequestId[ownProps.requestId];
  const fulfillments = fulfillmentIds ? fulfillmentIds.map((id) => fulfillmentsById[id]) : null;

  return {
    fulfillments,
    response: {samples},
  };
}

export default compose(
  withRequestLoading,
  AssayStore.connect((assayStore: typeof AssayStore) => ({
    requestSchemas: assayStore.getAssaySchemas(ASSAY_TYPES.ASSAY_REQUEST),
  })),
  StorableStore.connect((storableStore: typeof StorableStore) => ({batches: storableStore.getBatches()})),
  connect(mapStateToProps, {fetchRequestResponse})
)(RequestResponse) as React.ComponentClass<{requestId: string}>;
